import { useCallback, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGridV2, TONES } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column } from '@bloomays-lib/ui.shared/datagrid';
import { AnnualPrimeRowStatus, AnnualPrimeTableRow, AnnualTamPrimeAggregate } from '../../types/primes';
import { buildMonthlyPrimeNavigationTarget } from '../../utils/annualPrime.utils';
import { formatMonthYearToMonthLabel, formatPrimeEuro, formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';

const MISSING_VALUE_PLACEHOLDER = '—';

/** Un mois « missing » n'a pas été calculé : afficher un tiret plutôt qu'un montant à 0 trompeur. */
const formatMoneyOrPlaceholder = (row: AnnualPrimeTableRow, amount: number): string =>
  row.status === 'missing' ? MISSING_VALUE_PLACEHOLDER : formatPrimeEuro(amount);

export type AnnualTamPrimesTableProps = {
  rows: AnnualPrimeTableRow[];
  loading: boolean;
  annualAggregate?: AnnualTamPrimeAggregate;
  tamEmail: string;
  isTamPreview: boolean;
};

const leftAlignedColumn = {
  align: 'left' as const,
  headerAlign: 'left' as const,
};

const getPrimeStatusDotColor = (status: AnnualPrimeRowStatus, theme: Theme) => {
  if (status === 'validated') {
    return theme.palette.green[100];
  }
  if (status === 'missing') {
    return theme.palette.red[100];
  }
  return theme.palette.beige[100];
};

const getPrimeStatusLabelKey = (status: AnnualPrimeRowStatus): string => {
  if (status === 'validated') {
    return 'monthStatusValidated';
  }
  if (status === 'missing') {
    return 'monthStatusMissing';
  }
  return 'monthStatusPending';
};

const AnnualTamPrimesTable = ({
  rows,
  loading,
  annualAggregate,
  tamEmail,
  isTamPreview,
}: AnnualTamPrimesTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);
  const navigate = useNavigate();

  const handleNavigateToMonth = useCallback(
    (row: AnnualPrimeTableRow) => {
      if (loading || !tamEmail || !annualAggregate) {
        return;
      }
      const target = buildMonthlyPrimeNavigationTarget(row, tamEmail, isTamPreview, annualAggregate);
      navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
    },
    [annualAggregate, isTamPreview, loading, navigate, tamEmail],
  );

  const columns: DataGridV2Column<AnnualPrimeTableRow>[] = useMemo(
    () => [
      {
        field: 'monthYear',
        header: t('columnMonth'),
        minWidth: 120,
        flex: 0.8,
        variant: 'custom',
        render: (row) => (
          <MonthYearCell>
            <MonthYearText>{formatMonthYearToMonthLabel(row.monthYear)}</MonthYearText>
            <StatusDot
              $color={getPrimeStatusDotColor(row.status, theme)}
              title={t(getPrimeStatusLabelKey(row.status))}
            />
          </MonthYearCell>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'actions',
        header: t('columnAction'),
        width: 72,
        sortable: false,
        filterable: false,
        variant: 'custom',
        render: (row) => (
          <IconButton
            size="small"
            disabled={loading || !annualAggregate}
            aria-label={row.status === 'missing' ? t('validateMonth') : t('annualActionMonthlyDetail')}
            onClick={() => handleNavigateToMonth(row)}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'totalFreelanceMarge',
        header: t('columnFreelanceMarge'),
        minWidth: 130,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalFreelanceMarge,
        render: (row) => <MoneyText>{formatMoneyOrPlaceholder(row, row.totalFreelanceMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalBillingMarge',
        header: t('columnBillingMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalBillingMarge,
        render: (row) => <MoneyText>{formatMoneyOrPlaceholder(row, row.totalBillingMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalMarge',
        header: t('columnTotalMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalMarge,
        render: (row) => <MoneyText>{formatMoneyOrPlaceholder(row, row.totalMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalPrime',
        header: t('columnCommission'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalPrime,
        render: (row) => <MoneyText>{formatMoneyOrPlaceholder(row, row.totalPrime)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'bonus',
        header: t('columnBonus'),
        minWidth: 110,
        flex: 0.9,
        variant: 'custom',
        valueGetter: (row) => row.bonus,
        render: (row) => <MoneyText>{formatMoneyOrPlaceholder(row, row.bonus)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'regularisation',
        header: t('columnRegularisation'),
        minWidth: 140,
        flex: 1,
        valueGetter: (row) =>
          row.status === 'missing' ? MISSING_VALUE_PLACEHOLDER : formatPrimeEuroWithSign(row.regularisation),
        ...leftAlignedColumn,
      },
      {
        field: 'totalDeals',
        header: t('columnDeals'),
        minWidth: 100,
        flex: 0.7,
        valueGetter: (row) => (row.status === 'missing' ? MISSING_VALUE_PLACEHOLDER : row.totalDeals),
        ...leftAlignedColumn,
      },
      {
        field: 'totalMissions',
        header: t('columnMissions'),
        minWidth: 110,
        flex: 0.8,
        valueGetter: (row) => (row.status === 'missing' ? MISSING_VALUE_PLACEHOLDER : row.totalMissions),
        ...leftAlignedColumn,
      },
    ],
    [annualAggregate, handleNavigateToMonth, loading, t, theme],
  );

  return (
    <DataGridV2<AnnualPrimeTableRow>
      uniqueId="annual-tam-primes-table"
      rows={rows}
      columns={columns}
      loading={loading}
      loadingVariant="bar"
      getRowId={(row) => row.rowId}
      getRowTone={(row) => (row.status === 'missing' ? 'danger' : undefined)}
      search={false}
      enableExport={false}
      enableColumnSelector={false}
      enableFilterPanel={false}
      pagination={rows.length > 12}
      pageSize={12}
      hideFooter={rows.length <= 12}
    />
  );
};

export default AnnualTamPrimesTable;

/** Reproduit le style de `MoneyCell` (ui.shared) pour un montant à 2 décimales fixes. */
const MoneyText = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: TONES.success.color,
}));

const MonthYearCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const MonthYearText = styled('span')(() => ({
  fontSize: 13,
  whiteSpace: 'nowrap',
}));

const StatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color: string }>(({ $color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: $color,
}));
